import { captureException } from '@sentry/react';
import { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PageLoader from 'components/PageLoader';
import { GITHUB_COOKIE_KEY } from 'config/constants/github';
import { StorageVars } from 'config/constants/storage';
import { ERoute } from 'types/frontend';
import { handleAuthError, isAuthError } from 'utils/authError';
import { setCookie } from 'utils/cookies';

import { getAuthEventAction, isStaleUserId, shouldCheckOnboarding } from './authSessionUtils';

type AuthContextData = {
  loading: boolean;
  signOut: () => void;
  session: Session | null;
  userId: string | null;
};

export const AuthContext = React.createContext({} as AuthContextData);

export const AuthProvider = ({ children }) => {
  const currentUserId = localStorage.getItem(StorageVars.BDS_USER_ID);

  const [signingOut, setSigningOut] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [userId, setUserId] = useState<string | null>(currentUserId);
  const [loading, setLoading] = useState(true);

  // Ref tracks the latest userId so the onAuthStateChange closure
  // always sees the current value, not the stale mount-time value.
  const userIdRef = useRef<string | null>(currentUserId);

  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { toastSuccess, toastError } = useToastWrapper();

  useEffect(() => {
    const checkIfUserIsOnboarded = async (session: Session | null) => {
      if (session) {
        const sessionUserId = session.user.id;
        const currentId = userIdRef.current;

        if (shouldCheckOnboarding(currentId, session)) {
          if (isStaleUserId(currentId, sessionUserId)) {
            localStorage.removeItem(StorageVars.BDS_USER_ID);
            userIdRef.current = null;
            setUserId(null);
          }

          const { data, error } = await supabase
            .from('user_profile_view')
            .select('id')
            .eq('id', session.user.id)
            .maybeSingle();

          if (error) {
            if (isAuthError(error)) {
              handleAuthError();
              return;
            }
            throw error;
          }

          if (data?.id) {
            localStorage.setItem(StorageVars.BDS_USER_ID, data.id);
            userIdRef.current = data.id;
            setUserId(data.id);
          }
        }

        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    const updateSessionState = (session: Session | null) => {
      setSession(session);

      if (session?.provider_token) {
        setCookie(GITHUB_COOKIE_KEY, session.provider_token);
      }
    };

    const handleFullCheck = (session: Session | null) => {
      // Block rendering until the DB onboarding check completes.
      // Without this, a sign-out → sign-in cycle briefly renders with
      // session=true + userId=null, which shows AccountCreatingStep
      // for returning users who already have an account.
      if (session) {
        setLoading(true);
      }
      updateSessionState(session);
      checkIfUserIsOnboarded(session);
    };

    const handleSessionOnly = (session: Session | null) => {
      // TOKEN_REFRESHED: update session + cookie, skip DB query IF we already
      // know the user. Re-querying on every token refresh causes race conditions
      // and infinite refresh loops in multi-tab scenarios.
      //
      // However, if userId is still null (e.g. after account-create calls
      // refreshSession()), we MUST do a full check to pick up the new user.
      updateSessionState(session);

      if (userIdRef.current) {
        setLoading(false);
      } else {
        if (session) {
          setLoading(true);
        }
        checkIfUserIsOnboarded(session);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleFullCheck(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const action = getAuthEventAction(event);

      switch (action) {
        case 'sign_out':
          localStorage.removeItem(StorageVars.BDS_USER_ID);
          localStorage.removeItem(StorageVars.REACT_QUERY_CACHE);
          localStorage.removeItem(StorageVars.LOCAL_SUPABASE_AUTH_TOKEN);
          userIdRef.current = null;
          setSession(null);
          setUserId(null);
          break;

        case 'full_check':
          handleFullCheck(session);
          break;

        case 'session_only':
          handleSessionOnly(session);
          break;

        case 'ignore':
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      setSigningOut(true);
      await queryClient.invalidateQueries();
      queryClient.removeQueries();
      queryClient.clear();

      await supabase.auth.signOut();
      localStorage.removeItem(StorageVars.BDS_USER_ID);
      localStorage.removeItem(StorageVars.REACT_QUERY_CACHE);
      // Might be only locally but not sure still, worth to remove
      localStorage.removeItem(StorageVars.LOCAL_SUPABASE_AUTH_TOKEN);
      setSession(null);
      setUserId(null);
    } catch (error) {
      captureException(error);

      toastError(`Can't sign out, try again`);
    } finally {
      navigate(ERoute.Demo);
      setSigningOut(false);
      toastSuccess('Signed out successfully');
    }
  };

  if (signingOut) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider
      value={{
        loading,
        signOut,
        session,
        userId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextData => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
